/**
 * GraphQL Queries and Mutations for Critical Task Management
 * 
 * Handles distributed locking, task checkout/release, and critical workflow operations
 * through the GraphQL Federation gateway.
 */

import { gql } from '@apollo/client';

// Task Lock Operations
export const ACQUIRE_TASK_LOCK = gql`
  mutation AcquireTaskLock($input: AcquireTaskLockInput!) {
    acquireTaskLock(input: $input) {
      success
      lock {
        id
        taskId
        instanceId
        workflowType
        lockedBy
        lockedAt
        expiresAt
        lockType
        metadata {
          deviceId
          sessionId
          checkpoint
        }
      }
      error {
        code
        message
        conflictingLock {
          lockedBy
          expiresAt
        }
      }
    }
  }
`;

export const RELEASE_TASK_LOCK = gql`
  mutation ReleaseTaskLock($taskId: ID!, $userId: ID!) {
    releaseTaskLock(taskId: $taskId, userId: $userId) {
      success
      error {
        code
        message
      }
    }
  }
`;

export const EXTEND_TASK_LOCK = gql`
  mutation ExtendTaskLock($taskId: ID!, $extensionMinutes: Int!) {
    extendTaskLock(taskId: $taskId, extensionMinutes: $extensionMinutes) {
      success
      lock {
        id
        expiresAt
      }
      error {
        code
        message
      }
    }
  }
`;

export const GET_TASK_LOCKS = gql`
  query GetTaskLocks($filters: TaskLockFilters) {
    taskLocks(filters: $filters) {
      id
      taskId
      instanceId
      workflowType
      lockedBy
      lockedAt
      expiresAt
      lockType
      status
      metadata {
        deviceId
        sessionId
        checkpoint
      }
      user {
        id
        name
        email
      }
    }
  }
`;

// Critical Workflow Operations
export const EXECUTE_CRITICAL_TASK = gql`
  mutation ExecuteCriticalTask($input: ExecuteCriticalTaskInput!) {
    executeCriticalTask(input: $input) {
      success
      result {
        id
        instanceId
        taskId
        status
        completedAt
        executionTime
        checkpoints {
          id
          timestamp
          data
        }
      }
      error {
        code
        message
        retryable
      }
    }
  }
`;

export const QUEUE_CRITICAL_OPERATION = gql`
  mutation QueueCriticalOperation($input: QueueCriticalOperationInput!) {
    queueCriticalOperation(input: $input) {
      success
      queueItem {
        id
        taskId
        operation
        criticality
        queuedAt
        estimatedProcessingAt
      }
      error {
        code
        message
      }
    }
  }
`;

export const PROCESS_QUEUED_OPERATIONS = gql`
  mutation ProcessQueuedOperations($userId: ID!, $maxOperations: Int) {
    processQueuedOperations(userId: $userId, maxOperations: $maxOperations) {
      success
      processed {
        id
        taskId
        operation
        result
        processedAt
      }
      failed {
        id
        taskId
        operation
        error {
          code
          message
        }
      }
      remaining
    }
  }
`;

// Workflow Classification
export const GET_WORKFLOW_CRITICALITY = gql`
  query GetWorkflowCriticality($workflowType: String!, $taskType: String) {
    workflowCriticality(workflowType: $workflowType, taskType: $taskType) {
      id
      name
      level
      requiresConnection
      maxOfflineHours
      preventDuplicates
      lockTimeout
      restrictions {
        userRoles
        timeWindows
        deviceTypes
      }
    }
  }
`;

// Medication Safety Specific
export const ADMINISTER_MEDICATION = gql`
  mutation AdministerMedication($input: AdministerMedicationInput!) {
    administerMedication(input: $input) {
      success
      administration {
        id
        medicationId
        patientId
        dosage
        administeredAt
        administeredBy
        verificationStatus
        sideEffects
      }
      warnings {
        type
        message
        severity
      }
      error {
        code
        message
        blockingIssues {
          type
          description
          resolution
        }
      }
    }
  }
`;

export const VERIFY_MEDICATION_SAFETY = gql`
  query VerifyMedicationSafety($input: MedicationSafetyInput!) {
    verifyMedicationSafety(input: $input) {
      safe
      warnings {
        type
        message
        severity
        blockExecution
      }
      interactions {
        medicationId
        interactionType
        severity
        description
      }
      allergies {
        allergen
        severity
        reaction
      }
      dosageChecks {
        recommended
        maximum
        current
        withinRange
      }
    }
  }
`;

// Financial Transaction Safety
export const PROCESS_CRITICAL_PAYMENT = gql`
  mutation ProcessCriticalPayment($input: CriticalPaymentInput!) {
    processCriticalPayment(input: $input) {
      success
      transaction {
        id
        paymentId
        amount
        status
        processedAt
        confirmationNumber
        auditTrail {
          timestamp
          action
          userId
          details
        }
      }
      error {
        code
        message
        retryable
        duplicateTransaction {
          id
          processedAt
          amount
        }
      }
    }
  }
`;

export const VERIFY_PAYMENT_INTEGRITY = gql`
  query VerifyPaymentIntegrity($paymentId: ID!) {
    verifyPaymentIntegrity(paymentId: $paymentId) {
      valid
      status
      duplicateRisk
      fraudCheck {
        riskScore
        flags
        recommendations
      }
      balanceVerification {
        sufficient
        availableBalance
        requiredAmount
      }
    }
  }
`;

// Health and Status Monitoring
export const GET_CRITICAL_SYSTEM_STATUS = gql`
  query GetCriticalSystemStatus {
    criticalSystemStatus {
      online
      services {
        name
        status
        lastHealthCheck
        responseTime
      }
      locks {
        active
        expired
        averageLockTime
      }
      queue {
        critical
        sensitive
        standard
        oldestItem
        averageProcessingTime
      }
      integrity {
        checksumValid
        lastVerification
        corruptedItems
      }
    }
  }
`;

// Subscriptions for Real-time Updates
export const TASK_LOCK_UPDATES = gql`
  subscription TaskLockUpdates($userId: ID!) {
    taskLockUpdates(userId: $userId) {
      type
      lock {
        id
        taskId
        instanceId
        workflowType
        lockedBy
        lockedAt
        expiresAt
        status
      }
    }
  }
`;

export const CRITICAL_QUEUE_UPDATES = gql`
  subscription CriticalQueueUpdates($userId: ID!) {
    criticalQueueUpdates(userId: $userId) {
      type
      queueItem {
        id
        taskId
        operation
        criticality
        queuedAt
        status
      }
      stats {
        critical
        sensitive
        total
      }
    }
  }
`;

// Type definitions for TypeScript
export interface AcquireTaskLockInput {
  taskId: string;
  instanceId: string;
  workflowType: string;
  lockType: 'exclusive' | 'shared';
  timeout?: number;
  metadata?: {
    deviceId?: string;
    sessionId?: string;
    checkpoint?: string;
  };
}

export interface ExecuteCriticalTaskInput {
  taskId: string;
  instanceId: string;
  workflowType: string;
  taskType: string;
  data: Record<string, unknown>;
  lockId?: string;
  checkpoints?: Array<{
    id: string;
    data: Record<string, unknown>;
  }>;
}

export interface QueueCriticalOperationInput {
  taskId: string;
  operation: 'execute' | 'complete' | 'fail';
  data: Record<string, unknown>;
  criticality: 'critical' | 'sensitive' | 'standard';
  priority?: number;
  executeAfter?: string; // ISO timestamp
}

export interface AdministerMedicationInput {
  medicationId: string;
  patientId: string;
  dosage: string;
  route: string;
  administeredBy: string;
  verificationMethod: 'barcode' | 'manual' | 'biometric';
  witnesses?: string[];
  notes?: string;
  lockId: string;
}

export interface CriticalPaymentInput {
  paymentId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  recipientId: string;
  reference?: string;
  lockId: string;
  verificationLevel: 'standard' | 'enhanced' | 'manual';
}

export interface MedicationSafetyInput {
  medicationId: string;
  patientId: string;
  dosage: string;
  route: string;
  frequency?: string;
}

export interface TaskLockFilters {
  userId?: string;
  workflowType?: string;
  status?: 'active' | 'expired' | 'released';
  lockedAfter?: string;
  lockedBefore?: string;
}